import {
  useCallback,
  useState,
  useEffect,
  useEffect as useEffectExpose,
  useMemo,
} from "react";
import toast from "react-hot-toast";
import {
  employeeService,
  employeeCodeStructureService,
  attendanceStructureService,
  confirmationPolicyService,
  companyService,
} from "../../../services/api";
import FloatingInput from "../FloatingInput.jsx";
import DatePicker from "../../Common/DatePicker.jsx";
import SearchableSelect from "../SearchableSelect.jsx";
import FloatingInputWithNA from "../FloatingInputWithNA.jsx";
import { loadAttendancePolicies } from "../../../lib/attendancePolicyEngine";
import {
  getNewJoinerCutOffWarning,
  normalizeCutOffConfig,
} from "../../../lib/attendanceCutoff";
import {
  PAN_SENTINEL_NOT_AVAILABLE,
  PAN_SENTINEL_APPLIED,
  canonicalizePanValue,
  isPanSentinelForUniqueness,
  getIndividualEmployeePanError,
} from "../../../constants/panSentinels.js";
import {
  parseDateSafe,
  checkLockedAttendanceForEmploymentDateChange,
  buildLockedAttendanceDateToast,
} from "../../../utils/lockedAttendanceDateCheck.js";
import LockedAttendanceDateWarningModal from "../../Common/LockedAttendanceDateWarningModal.jsx";
import { previewPolicyConfirmationDate } from "../../../utils/confirmationPolicyPreview.js";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { isHeadOfOrganisationEmployee } from "../../../utils/headOfOrganisation";
import {
  isSameIndianMobile,
  MOBILE_SAME_AS_EMERGENCY_CONTACT,
} from "./validationHelpers";
import EmployeeSalaryStructureModal from "../employee-management/salary-structure/EmployeeSalaryStructureModal.jsx";
import { applyMatchedSalaryStructureForEmployee } from "./applyMatchedSalaryStructure.js";
import { getAuthItem, getAuthJson } from "../../../utils/authStorage";
import {
  getEmployeeCapError,
  getEssCapError,
} from "../../../utils/companyLicense";
import {
  canChooseStructureMode,
  normalizeSalaryStructureMethod,
  salaryAmountFieldLabel,
  SALARY_STRUCTURE_METHOD_CHANGED_EVENT,
} from "../../../utils/salaryStructureMethod";

// Helper function to convert number to words (Indian currency format)
const numberToWords = (num) => {
  if (!num || num === 0) return "";
  const numValue = parseFloat(num);
  if (isNaN(numValue) || numValue < 0) return "";
  const integerPart = Math.floor(numValue);
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const convertHundreds = (n) => {
    let result = "";
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      result += ones[n] + " ";
    }
    return result.trim();
  };
  if (integerPart === 0) return "";
  let result = "";
  let remaining = integerPart;
  if (remaining >= 10000000) {
    const crores = Math.floor(remaining / 10000000);
    result += convertHundreds(crores) + " Crore ";
    remaining %= 10000000;
  }
  if (remaining >= 100000) {
    const lakhs = Math.floor(remaining / 100000);
    result += convertHundreds(lakhs) + " Lakh ";
    remaining %= 100000;
  }
  if (remaining >= 1000) {
    const thousands = Math.floor(remaining / 1000);
    result += convertHundreds(thousands) + " Thousand ";
    remaining %= 1000;
  }
  if (remaining > 0) {
    result += convertHundreds(remaining);
  }
  return result.trim();
};

/** True when relieved date is set and is on or before today (start of local day). */
function isRelievedDateOnOrBeforeToday(relievedDateStr) {
  if (!relievedDateStr) return false;
  const d = new Date(relievedDateStr);
  if (Number.isNaN(d.getTime())) return false;
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() <= today.getTime();
}

function isRelievedDateAfterToday(relievedDateStr) {
  if (!relievedDateStr) return false;
  const d = new Date(relievedDateStr);
  if (Number.isNaN(d.getTime())) return false;
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() > today.getTime();
}

export function OrgDetailsForm({
  employeeData,
  setEmployeeData,
  onSaveSection,
  rejoinSelectedEmployeeId,
  loading,
  feedback,
  darkMode,
  onCancel,
  onNext,
  isLastTab,
  readOnly = false,
  isEmployeeView = false,
  masterData = {},
  masterDataLoading = false,
  onSaveTrigger,
  onNavigateToTransfer,
  /** Called after employee org details persist successfully (including after modal "Continue" flows). Lets parent reset footer Edit vs Update. */
  onSaveSuccess,
  isOnboardingMode = false,
  
}) {
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canViewSalaryStructure =
    isAdmin || hasScreenAccess("payroll", "salary_structure", "view");
  const [salaryStructureAutoOpenKey, setSalaryStructureAutoOpenKey] = useState(0);

  // Use employeeData for form values - this ensures formData is always current
  const formData = employeeData;
  const [formErrors, setFormErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [employeeCodeStructure, setEmployeeCodeStructure] = useState(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [showStatutoryWarningModal, setShowStatutoryWarningModal] = useState({
    show: false,
    field: null,
    message: '',
    title: '',
  });
  const [pendingCheckboxChange, setPendingCheckboxChange] = useState(null);
  
  // Master details tracking
  const [initialMasterDetails, setInitialMasterDetails] = useState({});
  const [snapshotEmployeeId, setSnapshotEmployeeId] = useState(null);
  const [masterDetailsChanged, setMasterDetailsChanged] = useState(false);
  const [showMasterDetailsWarning, setShowMasterDetailsWarning] = useState(false);
  const [showConfirmedToProbationWarning, setShowConfirmedToProbationWarning] =
    useState(false);
  const [showRelievedLockedWarning, setShowRelievedLockedWarning] =
    useState(false);
  const [showRejoinBeforeRelieveWarning, setShowRejoinBeforeRelieveWarning] =
    useState(false);
  const [confirmedMasterChange, setConfirmedMasterChange] = useState(false);
  const [showLockedAttendanceDateWarning, setShowLockedAttendanceDateWarning] =
    useState(false);
  const [lockedAttendanceWarningMonths, setLockedAttendanceWarningMonths] =
    useState([]);

  // Track initial CTC for change detection
  const [initialCTC, setInitialCTC] = useState(null);
  /** DB employment status when this employee record was loaded (edit mode only). */
  const [initialEmploymentStatus, setInitialEmploymentStatus] = useState(null);
  const [initialEmploymentDates, setInitialEmploymentDates] = useState({
    joiningDate: null,
    relievedDate: null,
  });
  const [initialIdentitySnapshot, setInitialIdentitySnapshot] = useState({
    officialEmail: "",
    personalEmail: "",
    pan: "",
    aadhaar: "",
    mobile: "",
  });
  const [confirmationPolicies, setConfirmationPolicies] = useState([]);
  const [orgSalaryMethod, setOrgSalaryMethod] = useState(() => {
    const cached = getAuthJson("companyData");
    return normalizeSalaryStructureMethod(cached?.defaultSalaryStructureMethod);
  });
  const [profileSalaryMode, setProfileSalaryMode] = useState("CTC");

  useEffect(() => {
    let cancelled = false;
    const loadMethod = async () => {
      const companyId =
        Number(getAuthItem("companyId")) ||
        Number(getAuthJson("currentUser")?.companyId) ||
        Number(getAuthJson("companyData")?.id) ||
        0;
      if (!companyId) return;
      try {
        const res = await companyService.getMinimumWagesSetting(companyId);
        const method = normalizeSalaryStructureMethod(
          res?.data?.defaultSalaryStructureMethod
        );
        if (!cancelled) setOrgSalaryMethod(method);
      } catch {
        /* keep cached default */
      }
    };
    loadMethod();
    const onChanged = (e) => {
      if (e?.detail?.defaultSalaryStructureMethod) {
        setOrgSalaryMethod(
          normalizeSalaryStructureMethod(e.detail.defaultSalaryStructureMethod)
        );
      }
    };
    window.addEventListener(SALARY_STRUCTURE_METHOD_CHANGED_EVENT, onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(SALARY_STRUCTURE_METHOD_CHANGED_EVENT, onChanged);
    };
  }, []);

  const ctcFieldLabel = salaryAmountFieldLabel(orgSalaryMethod, profileSalaryMode);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await confirmationPolicyService.getConfirmationPolicies();
        if (!cancelled && res?.success) {
          setConfirmationPolicies(res.data || []);
        }
      } catch (err) {
        console.warn("Failed to load confirmation policies for org form:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const policyConfirmationPreview = useMemo(
    () => previewPolicyConfirmationDate(formData, confirmationPolicies),
    [
      confirmationPolicies,
      formData?.employmentStatus,
      formData?.joiningDate,
      formData?.dateOfJoin,
      formData?.branchId,
      formData?.departmentId,
      formData?.designationId,
      formData?.gradeId,
      formData?.categoryId,
      formData?.subDepartmentId,
      formData?.employmentType,
      formData?.id,
    ]
  );

  const normalizeEmailForCompare = (value) => {
    const v = String(value || "").trim().toLowerCase();
    if (!v || v === "na" || v === "not available") return "";
    return v;
  };

  const normalizePanForCompare = (value) => {
    if (isPanSentinelForUniqueness(value)) return "";
    const v = canonicalizePanValue(value);
    if (isPanSentinelForUniqueness(v)) return "";
    return String(v || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "");
  };

  const normalizeAadhaarForCompare = (value) => {
    const v = String(value || "").replace(/\D/g, "");
    return v || "";
  };

  /** Last 10 digits for India; strips leading 91 when present. */
  const normalizeMobileForCompare = (value) => {
    let digits = String(value || "").replace(/\D/g, "");
    if (digits.length >= 12 && digits.startsWith("91")) {
      digits = digits.slice(-10);
    } else if (digits.length > 10) {
      digits = digits.slice(-10);
    }
    return digits.length === 10 ? digits : "";
  };

  const validateUniqueIdentityFields = async () => {
    const currentId = employeeData?.id != null ? String(employeeData.id) : null;
    const rejoinFromIdCandidates = [
      rejoinSelectedEmployeeId,
      employeeData?.rejoinFromEmployeeId,
      employeeData?.rejoinFromId,
      employeeData?.sourceEmployeeId,
      employeeData?.parentEmployeeId,
      employeeData?.previousEmployeeId,
    ]
      .filter((v) => v != null && v !== "")
      .map((v) => String(v));

    const targetOfficialEmail = normalizeEmailForCompare(formData.email);
    const targetPersonalEmail = normalizeEmailForCompare(formData.user?.email);
    const targetPan = normalizePanForCompare(formData.panNumber);
    const targetAadhaar = normalizeAadhaarForCompare(formData.aadharNumber);
    const targetMobile = normalizeMobileForCompare(formData.user?.phone);

    // Only when editing or explicit rejoin: infer historical source row IDs to ignore (API may omit rejoinFrom).
    // Pure "Add employee" without rejoin must NOT whitelist relieved/resigned/terminated rows — that was blocking real duplicates.
    const allowImplicitHistoricalRejoinMatch =
      Boolean(currentId) || rejoinFromIdCandidates.length > 0;

    const implicitRejoinSourceIds = new Set();

    if (allowImplicitHistoricalRejoinMatch) {
      const token =
        (formData.panNumber || "").trim() ||
        String(formData.aadharNumber || "").replace(/\D/g, "") ||
        String(formData.user?.phone || "").replace(/\D/g, "") ||
        targetOfficialEmail ||
        targetPersonalEmail ||
        "";
      if (token) {
        try {
          const r = await employeeService.getEmployees({
            page: 1,
            limit: 100,
            search: token,
          });
          const candidates = r?.employees || r?.data || [];
          for (const emp of candidates) {
            const ownerId = emp?.id ?? emp?.employeeId ?? emp?.userId;
            if (!ownerId) continue;
            if (currentId && String(ownerId) === currentId) continue;

            const status = String(emp?.employmentStatus || "")
              .trim()
              .toLowerCase();
            const isHistorical =
              emp?.editLocked === true ||
              status === "relieved" ||
              status === "resigned" ||
              status === "terminated";
            if (!isHistorical) continue;

            const empOfficialEmail = normalizeEmailForCompare(emp?.email);
            const empPersonalEmail = normalizeEmailForCompare(emp?.user?.email);
            const empPan = normalizePanForCompare(emp?.panNumber);
            const empAadhaar = normalizeAadhaarForCompare(emp?.aadharNumber);
            const empMobile = normalizeMobileForCompare(
              emp?.user?.phone ?? emp?.phone ?? emp?.mobileNumber
            );

            const identityMatched =
              (targetPan && targetPan === empPan) ||
              (targetAadhaar && targetAadhaar === empAadhaar) ||
              (targetMobile && targetMobile === empMobile) ||
              (targetOfficialEmail &&
                (targetOfficialEmail === empOfficialEmail ||
                  targetOfficialEmail === empPersonalEmail)) ||
              (targetPersonalEmail &&
                (targetPersonalEmail === empOfficialEmail ||
                  targetPersonalEmail === empPersonalEmail));

            if (identityMatched) {
              implicitRejoinSourceIds.add(String(ownerId));
            }
          }
        } catch (e) {
          console.warn("Implicit rejoin candidate fetch:", e);
        }
      }
    }

    const ignoreEmployeeIds = [
      ...new Set([
        ...rejoinFromIdCandidates,
        ...Array.from(implicitRejoinSourceIds),
        ...(currentId ? [currentId] : []),
      ]),
    ]
      .filter(Boolean)
      .join(",");

    const dupRes = await employeeService.checkEmployeeIdentityDuplicates({
      officialEmail: formData.email || "",
      personalEmail: formData.user?.email || "",
      pan: formData.panNumber || "",
      aadhar: formData.aadharNumber || "",
      mobile: formData.user?.phone || "",
      ignoreEmployeeIds,
    });

    const conflicts = dupRes?.conflicts || [];
    if (conflicts.length === 0) return;

    const duplicateErrors = {};
    const seen = new Set();
    for (const c of conflicts) {
      for (const m of c.matches || []) {
        if (seen.has(m)) continue;
        seen.add(m);
        if (m === "officialEmail") {
          duplicateErrors.email =
            "Official email already exists for another employee. Use a unique email.";
        } else if (m === "personalEmail") {
          duplicateErrors.personalEmail =
            "Personal email already exists for another employee. Use a unique email.";
        } else if (m === "pan") {
          duplicateErrors.panNumber =
            "PAN already exists for another employee. Use a unique PAN.";
        } else if (m === "aadhar") {
          duplicateErrors.aadharNumber =
            "Aadhaar already exists for another employee. Use a unique Aadhaar.";
        } else if (m === "mobile") {
          duplicateErrors.mobileNumber =
            "Mobile number already exists for another employee. Use a unique mobile number.";
        }
      }
    }

    if (Object.keys(duplicateErrors).length > 0) {
      setFormErrors((prev) => ({ ...prev, ...duplicateErrors }));
      setTouchedFields((prev) => ({
        ...prev,
        ...(duplicateErrors.email ? { email: true } : {}),
        ...(duplicateErrors.personalEmail ? { personalEmail: true } : {}),
        ...(duplicateErrors.panNumber ? { panNumber: true } : {}),
        ...(duplicateErrors.aadharNumber ? { aadharNumber: true } : {}),
        ...(duplicateErrors.mobileNumber ? { mobileNumber: true } : {}),
      }));
      throw new Error(
        "Duplicate email/mobile/PAN/Aadhaar found. These values must be unique unless rejoining from the same employee."
      );
    }
  };

  // Use totalEmployees from masterData instead of local state
  const totalEmployees = masterData.totalEmployees || 0;
  const companyForHo =
    masterData?.company || getAuthJson("companyData", null);
  const isOrgHead = isHeadOfOrganisationEmployee(
    employeeData?.id,
    companyForHo
  );

  // HO must never retain a Reporting Manager on the form
  useEffect(() => {
    if (!isOrgHead) return;
    if (employeeData?.reportingManagerId == null || employeeData?.reportingManagerId === "") {
      return;
    }
    setEmployeeData((prev) =>
      prev ? { ...prev, reportingManagerId: null } : prev
    );
  }, [isOrgHead, employeeData?.reportingManagerId, setEmployeeData]);

  // Initialize statutory compliance fields with default values if they don't exist
  useEffect(() => {
    if (
      employeeData &&
      (!Object.prototype.hasOwnProperty.call(employeeData, "pfApplicable") ||
        !Object.prototype.hasOwnProperty.call(employeeData, "ceilingPf") ||
        !Object.prototype.hasOwnProperty.call(employeeData, "epsApplicable") ||
        !Object.prototype.hasOwnProperty.call(employeeData, "esiApplicable") ||
        !Object.prototype.hasOwnProperty.call(employeeData, "ptApplicable") ||
        !Object.prototype.hasOwnProperty.call(
          employeeData,
          "gratuityApplicable"
        ))
    ) {
      setEmployeeData((prev) => ({
        ...prev,
        pfApplicable: prev.pfApplicable !== false, // Default to true
        ceilingPf: prev.ceilingPf !== false, // Default to true
        epsApplicable: prev.epsApplicable || false, // Default to false
        esiApplicable: prev.esiApplicable || false, // Default to false
        ptApplicable: prev.ptApplicable !== false, // Default to true
        gratuityApplicable: prev.gratuityApplicable !== false, // Default to true
      }));
    }
  }, [employeeData, setEmployeeData]);

  // Map legacy PAN strings from API ("Not Available", "PAN Applied", etc.) to canonical tokens for display
  useEffect(() => {
    if (!employeeData?.id || employeeData.panNumber == null) return;
    const raw = String(employeeData.panNumber).trim();
    if (!raw) return;
    const canon = canonicalizePanValue(raw);
    if (canon !== raw) {
      setEmployeeData((prev) =>
        prev && prev.id === employeeData.id ? { ...prev, panNumber: canon } : prev
      );
    }
  }, [employeeData?.id, employeeData?.panNumber, setEmployeeData]);

  // Initialize employment status with default value if not set
  useEffect(() => {
    if (employeeData && !employeeData.employmentStatus) {
      setEmployeeData((prev) => ({
        ...prev,
        employmentStatus: "Probation",
      }));
    }
  }, [employeeData, setEmployeeData]);

  // Initialize Send Invite only if not set (do not override user selection / defaults)
  useEffect(() => {
    if (!employeeData) return;
    setEmployeeData((prev) => {
      if (!prev) return prev;
      if (prev.inviteSent !== undefined && prev.inviteSent !== null) return prev;
      return { ...prev, inviteSent: false };
    });
  }, [employeeData?.id, setEmployeeData]);

  // Initialize snapshot of master details ONCE per employee id
  useEffect(() => {
    if (employeeData && employeeData.id) {
      if (snapshotEmployeeId !== employeeData.id) {
        const masterFields = {
          branchId: employeeData.branchId,
          designationId: employeeData.designationId,
          subDepartmentId: employeeData.subDepartmentId,
          departmentId: employeeData.departmentId,
          gradeId: employeeData.gradeId,
          categoryId: employeeData.categoryId,
          reportingManagerId: employeeData.reportingManagerId,
        };
        setInitialMasterDetails(masterFields);
        setInitialCTC(employeeData.ctc || null); // Track initial CTC
        setInitialEmploymentStatus(employeeData.employmentStatus ?? null);
        setInitialEmploymentDates({
          joiningDate: employeeData.joiningDate || null,
          relievedDate: employeeData.relievedDate || null,
        });
        setInitialIdentitySnapshot({
          officialEmail: normalizeEmailForCompare(employeeData.email),
          personalEmail: normalizeEmailForCompare(employeeData.user?.email),
          pan: normalizePanForCompare(employeeData.panNumber),
          aadhaar: normalizeAadhaarForCompare(employeeData.aadharNumber),
          mobile: normalizeMobileForCompare(employeeData.user?.phone),
        });
        setSnapshotEmployeeId(employeeData.id);
      }
    } else {
      // For new employee, clear snapshot
      if (snapshotEmployeeId !== null) {
        setSnapshotEmployeeId(null);
        setInitialMasterDetails({});
        setInitialCTC(null);
        setInitialEmploymentStatus(null);
        setInitialEmploymentDates({ joiningDate: null, relievedDate: null });
        setInitialIdentitySnapshot({
          officialEmail: "",
          personalEmail: "",
          pan: "",
          aadhaar: "",
          mobile: "",
        });
      }
    }
  }, [employeeData?.id]);

  // Check for master details changes
  useEffect(() => {
    if (employeeData && employeeData.id && Object.keys(initialMasterDetails).length > 0) {
      const currentMasterFields = {
        branchId: employeeData.branchId,
        designationId: employeeData.designationId,
        subDepartmentId: employeeData.subDepartmentId,
        departmentId: employeeData.departmentId,
        gradeId: employeeData.gradeId,
        categoryId: employeeData.categoryId,
        reportingManagerId: employeeData.reportingManagerId,
      };

      const hasChanges = Object.keys(currentMasterFields).some(
        (key) => currentMasterFields[key] !== initialMasterDetails[key]
      );
      
      setMasterDetailsChanged(hasChanges);
    }
  }, [employeeData.branchId, employeeData.designationId, employeeData.subDepartmentId, employeeData.departmentId, employeeData.gradeId, employeeData.categoryId, employeeData.reportingManagerId, initialMasterDetails]);

  // Memoize the reporting employees fetch function to prevent unnecessary API calls
  const fetchReportingEmployees = useCallback(() => {
    return employeeService.getReportingEmployees(employeeData.id);
  }, [employeeData.id]);

  // Fetch employee code structure
  const fetchEmployeeCodeStructure = useCallback(async () => {
    try {
      const data = await employeeCodeStructureService.getCurrentStructure();
      setEmployeeCodeStructure(data.data);
      return data.data;
    } catch (error) {
      console.error("Error fetching employee code structure:", error);
    }
    return null;
  }, []);

  // Generate automated employee code
  const generateAutomatedEmployeeCode = useCallback(async () => {
    if (
      !employeeCodeStructure ||
      employeeCodeStructure.patternType !== "automated"
    ) {
      return;
    }

    setIsGeneratingCode(true);
    try {
      const data = await employeeCodeStructureService.getNextEmployeeCode();
      const nextCode = data.data.nextCode;

      setEmployeeData((prev) => ({
        ...prev,
        empCode: nextCode,
      }));
    } catch (error) {
      console.error("Error generating employee code:", error);
      toast.error(error.message || "Failed to generate employee code");
    } finally {
      setIsGeneratingCode(false);
    }
  }, [employeeCodeStructure, setEmployeeData]);

  // Fetch employee code structure on component mount
  useEffect(() => {
    if (!isEmployeeView) {
      // Only for admin users, not employee view
      fetchEmployeeCodeStructure();
    }
  }, [fetchEmployeeCodeStructure, isEmployeeView]);

  // Generate automated employee code when structure is loaded and code is empty
  // for new/rejoin flows. Rejoin prefill explicitly clears empCode, so this
  // must rerun even when employeeData.id state didn't change.
  useEffect(() => {
    const currentCode = String(employeeData?.empCode || "").trim();
    if (
      isOnboardingMode ||
      !employeeCodeStructure ||
      employeeCodeStructure.patternType !== "automated" ||
      isEmployeeView ||
      employeeData?.id ||
      currentCode
    ) {
      return;
    }
    generateAutomatedEmployeeCode();
  }, [
    employeeCodeStructure,
    isEmployeeView,
    isOnboardingMode,
    employeeData?.id,
    employeeData?.empCode,
    rejoinSelectedEmployeeId,
    generateAutomatedEmployeeCode,
  ]);

  // Validation functions
  const validateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "Date of Birth is required";

    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    if (age < 18) {
      return "Employee must be at least 18 years old";
    }
    if (age > 100) {
      return "Employee age cannot be more than 100 years";
    }
    return null;
  };

  const validateEmail = (email) => {
    const raw = email || "";
    const trimmed = raw.trim();
    const lowered = trimmed.toLowerCase();

    // Allow special sentinel values for missing email
    if (lowered === "na" || lowered === "not available") {
      return null;
    }

    if (!trimmed) return "Email is required";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      return "Please enter a valid email address";
    }
    return null;
  };

  const validateMobileNumber = (phone) => {
    if (!phone) return "Mobile number is required";
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(phone)) {
      return "Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9";
    }
    if (isSameIndianMobile(phone, formData.emergencyContactPhone)) {
      return MOBILE_SAME_AS_EMERGENCY_CONTACT;
    }
    return null;
  };

  const validatePAN = (pan) => {
    const raw = pan || "";
    const trimmed = raw.trim();
    const canon = canonicalizePanValue(trimmed);

    if (isPanSentinelForUniqueness(canon)) return null;

    if (!trimmed) return "PAN is required";

    const upper = trimmed.toUpperCase().replace(/\s+/g, "");
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(upper)) {
      return "Please enter a valid PAN number (e.g., ABCDE1234F)";
    }
    const individualPanErr = getIndividualEmployeePanError(upper);
    if (individualPanErr) return individualPanErr;
    // Check for common invalid patterns
    const invalidPatterns = [
      "AAAAA0000A",
      "BBBBB0000B",
      "CCCCC0000C",
      "DDDDD0000D",
      "EEEEE0000E",
      "FFFFF0000F",
      "GGGGG0000G",
      "HHHHH0000H",
      "IIIII0000I",
      "JJJJJ0000J",
      "KKKKK0000K",
      "LLLLL0000L",
      "MMMMM0000M",
      "NNNNN0000N",
      "OOOOO0000O",
      "PPPPP0000P",
      "QQQQQ0000Q",
      "RRRRR0000R",
      "SSSSS0000S",
      "TTTTT0000T",
      "UUUUU0000U",
      "VVVVV0000V",
      "WWWWW0000W",
      "XXXXX0000X",
      "YYYYY0000Y",
      "ZZZZZ0000Z",
    ];
    if (invalidPatterns.includes(upper)) {
      return "Please enter a valid PAN number";
    }
    return null;
  };

  const validateAadhaar = (aadhaar) => {
    if (!aadhaar) return "Aadhaar number is required";
    const aadhaarRegex = /^[0-9]{12}$/;
    if (!aadhaarRegex.test(aadhaar)) {
      return "Please enter a valid 12-digit Aadhaar number";
    }
    // Check for common invalid patterns
    if (
      aadhaar === "000000000000" ||
      aadhaar === "111111111111" ||
      aadhaar === "999999999999"
    ) {
      return "Please enter a valid Aadhaar number";
    }
    return null;
  };

  const validateEmployeeCode = (empCode) => {
    if (!empCode) return "Employee Code is required";
    if (empCode.length < 1) {
      return "Employee Code must be at least 1 character long";
    }
    if (empCode.length > 20) {
      return "Employee Code cannot exceed 20 characters";
    }
    // Check if employee code contains only alphanumeric characters, hyphens, forward slashes, and dots
    const allowedRegex = /^[a-zA-Z0-9\-/.]+$/;
    if (!allowedRegex.test(empCode)) {
      return "Employee Code can only contain letters, numbers, hyphens (-), forward slashes (/), and dots (.)";
    }
    return null;
  };

  const normalizeNameInput = (name) => {
    if (typeof name !== "string") return "";
    // Trim + collapse extra spaces (keeps only single spaces between words)
    return name.replace(/\s+/g, " ").trim();
  };

  // Title case for display while typing: "kranthi dev sai" -> "Kranthi Dev Sai"
  const toTitleCaseName = (name) => {
    if (typeof name !== "string") return "";
    const collapsed = name.replace(/\s+/g, " ");
    return collapsed
      .split(" ")
      .map((word) =>
        word.length
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : ""
      )
      .join(" ")
      .replace(/\s+/g, " "); // collapse again after split (handles leading/trailing)
  };

  const toCamelCaseName = (name) => {
    const normalized = normalizeNameInput(name);
    if (!normalized) return "";

    const words = normalized.split(" ").filter(Boolean);
    if (words.length === 0) return "";

    const first = words[0].toLowerCase();
    const rest = words
      .slice(1)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

    return [first, ...rest].join("");
  };

  // Returns camelCase name only if existing validation passes; otherwise returns null.
  const getCamelCaseNameIfValid = (name) => {
    const error = validateName(name);
    if (error) return null;
    return toCamelCaseName(name);
  };

  const validateName = (name) => {
    const normalized = normalizeNameInput(name);
    if (!normalized) return "Name is required";
    if (normalized.length < 2) {
      return "Name must be at least 2 characters long";
    }
    if (normalized.length > 50) {
      return "Name cannot exceed 50 characters";
    }
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!nameRegex.test(normalized)) {
      return "Name can only contain letters and spaces";
    }
    return null;
  };

  const validateJoiningDate = (joiningDate, dateOfBirth) => {
    if (!joiningDate) return "Joining Date is required";

    const joinDate = new Date(joiningDate);
    const today = new Date();

    // Joining date should not be in the future
    if (joinDate > today) {
      return "Joining Date cannot be in the future";
    }

    if (dateOfBirth) {
      const birthDate = new Date(dateOfBirth);
      let ageAtJoining = joinDate.getFullYear() - birthDate.getFullYear();
      const monthDiff = joinDate.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && joinDate.getDate() < birthDate.getDate())
      ) {
        ageAtJoining--;
      }

      if (ageAtJoining < 18) {
        return "Employee must be at least 18 years old at joining date";
      }
    }

    // Policy: block joining after attendance cut-off day
    try {
      const policies = loadAttendancePolicies() || [];
      const active = policies.find(
        (p) =>
          p &&
          p.status !== "inactive" &&
          normalizeCutOffConfig(p).cutOffEnabled &&
          normalizeCutOffConfig(p).cutOffNewJoinerHandling === "block_joining"
      );
      const warn = getNewJoinerCutOffWarning(joiningDate, active);
      if (warn) return warn;
    } catch {
      /* ignore policy lookup failures */
    }

    return null;
  };

  const validateConfirmationDate = (confirmationDate, joiningDate) => {
    if (!confirmationDate) return "Confirmation Date is required";

    const confirmDate = new Date(confirmationDate);
    const joinDate = new Date(joiningDate);
    const today = new Date();

    if (confirmDate < joinDate) {
      return "Confirmation Date cannot be before Joining Date";
    }

    // if (confirmDate > today) {
    //     return 'Confirmation Date cannot be in the future';
    // }

    return null;
  };

  const validateResignationDate = (resignationDate, joiningDate) => {
    if (!resignationDate) return "Resignation Date is required";

    const resignDate = new Date(resignationDate);
    const joinDate = new Date(joiningDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    resignDate.setHours(0, 0, 0, 0);

    if (resignDate < joinDate) {
      return "Resignation Date cannot be before Joining Date";
    }

    // ✅ NEW: Check if resignation date is in the past (backdated)
    // if (resignDate < today) {
    //   return "Resignation Date cannot be in the past. Backdated resignations are not allowed.";
    // }

    return null;
  };

  const validateRelievedDate = (relievedDate, resignationDate, joiningDate) => {
    if (!relievedDate) return "Relieved Date is required";

    const relieveDate = new Date(relievedDate);
    const joinDate = new Date(joiningDate);
    const today = new Date();

    if (relieveDate < joinDate) {
      return "Relieved Date cannot be before Joining Date";
    }

    if (resignationDate) {
      const resignDate = new Date(resignationDate);
      if (relieveDate < resignDate) {
        return "Relieved Date cannot be before Resignation Date";
      }
    }

    // if (relieveDate > today) {
    //     return 'Relieved Date cannot be in the future';
    // }

    return null;
  };

  const validateCTC = (ctc) => {
    if (!ctc) return "CTC is required";
    if (isNaN(ctc) || parseFloat(ctc) <= 0) {
      return "CTC must be a valid positive number";
    }
    if (parseFloat(ctc) > 999999999) {
      return "CTC cannot exceed 999,999,999";
    }
    return null;
  };

  // Validate individual field
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case "empCode":
        return validateEmployeeCode(value);
      case "name":
        return validateName(value);
      case "dateOfBirth":
        return validateAge(value);
      case "email":
        return validateEmail(value);
      case "personalEmail":
        return validateEmail(value);
      case "mobileNumber":
        return validateMobileNumber(value);
      case "panNumber":
        return validatePAN(value);
      case "aadharNumber":
        return validateAadhaar(value);
      case "bloodGroup":
        return !value ? "Blood Group is required" : null;
      case "joiningDate":
        return validateJoiningDate(value, formData.user?.dateOfBirth);
      case "confirmationDate":
        return validateConfirmationDate(value, formData.joiningDate);
      case "resignationDate":
        return validateResignationDate(value, formData.joiningDate);
      case "relievedDate":
        return validateRelievedDate(
          value,
          formData.resignationDate,
          formData.joiningDate
        );
      case "ctc":
        return validateCTC(value);
      case "branchId":
      case "designationId":
      case "departmentId":
      case "subDepartmentId":
      case "gradeId":
      case "categoryId":
      case "employmentType":
      case "employmentStatus":
      case "gender":
        return !value
          ? `${
              fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
            } is required`
          : null;
      default:
        return null;
    }
  };

  // Comprehensive validation function for entire form
  const validateForm = () => {
    const errors = {};

    // Basic validations
    if (!isOnboardingMode) {
      errors.empCode = validateEmployeeCode(formData.empCode);
    }
    errors.name = validateName(formData.user?.name);
    errors.dateOfBirth = validateAge(formData.user?.dateOfBirth);
    errors.email = validateEmail(formData.email);
    errors.personalEmail = validateEmail(formData.user?.email);
    errors.mobileNumber = validateMobileNumber(formData.user?.phone);
    errors.panNumber = validatePAN(formData.panNumber);
    errors.aadharNumber = validateAadhaar(formData.aadharNumber);
    errors.ctc = validateCTC(formData.ctc);
    errors.joiningDate = validateJoiningDate(
      formData.joiningDate,
      formData.user?.dateOfBirth
    );

    // Conditional validations
    if (formData.employmentStatus === "Confirmed") {
      if (!formData.confirmationDate) {
        errors.confirmationDate =
          "Confirmation Date is required for confirmed employees";
      } else {
        errors.confirmationDate = validateConfirmationDate(
          formData.confirmationDate,
          formData.joiningDate
        );
      }
    }

    if (
      formData.employmentStatus === "Resigned" ||
      formData.employmentStatus === "Relieved"
    ) {
      if (!formData.resignationDate) {
        errors.resignationDate =
          "Resignation Date is required for resigned/relieved employees";
      } else {
        errors.resignationDate = validateResignationDate(
          formData.resignationDate,
          formData.joiningDate
        );
      }

      if (!formData.relievedDate) {
        errors.relievedDate =
          "Relieved Date is required for resigned/relieved employees";
      } else {
        errors.relievedDate = validateRelievedDate(
          formData.relievedDate,
          formData.resignationDate,
          formData.joiningDate
        );
      }

      if (!formData.reason) {
        errors.reason =
          "Resignation Reason is required for resigned/relieved employees";
      }
    }

    if (formData.employmentStatus === "Terminated") {
      if (!formData.terminationDate) {
        errors.terminationDate =
          "Termination Date is required for terminated employees";
      } else {
        errors.terminationDate = validateRelievedDate(
          formData.terminationDate,
          formData.resignationDate,
          formData.joiningDate
        );
      }

      if (!formData.reason) {
        errors.reason =
          "Termination Reason is required for terminated employees";
      }
    }

    // Required field validations
    if (!formData.branchId) errors.branchId = "Branch is required";
    if (!formData.designationId)
      errors.designationId = "Designation is required";
    if (!formData.departmentId) errors.departmentId = "Department is required";
    if (!formData.subDepartmentId)
      errors.subDepartmentId = "Sub Department is required";
    if (!formData.gradeId) errors.gradeId = "Grade is required";
    if (!formData.categoryId) errors.categoryId = "Category is required";
    if (!formData.employmentType)
      errors.employmentType = "Employment Type is required";

    if (!formData.employmentStatus) {
      errors.employmentStatus = "Employment Status is required";
    }

    if (!formData.user?.gender) errors.gender = "Gender is required";
    if (!formData.user?.bloodGroup)
      errors.bloodGroup = "Blood Group is required";

    // Head of Organisation: RM must stay blank. Everyone else: required when
    // the company already has employees (onboarding workflow RM steps).
    // Onboarding mode: always require RM (policy routes to Reporting Manager).
    if (isOrgHead) {
      if (formData.reportingManagerId) {
        errors.reportingManagerId =
          "Head of Organisation cannot have a Reporting Manager";
      }
    } else if (
      isOnboardingMode ||
      (totalEmployees > 0 && !formData.reportingManagerId)
    ) {
      if (!formData.reportingManagerId) {
        errors.reportingManagerId = "Reporting Manager is required";
      }
    }

    // Additional validation for employment status specific fields
    if (
      formData.employmentStatus === "Confirmed" &&
      !formData.confirmationDate
    ) {
      errors.confirmationDate =
        "Confirmation Date is required for confirmed employees";
    }

    if (
      formData.employmentStatus === "Resigned" ||
      formData.employmentStatus === "Relieved"
    ) {
      if (!formData.resignationDate) {
        errors.resignationDate =
          "Resignation Date is required for resigned/relieved employees";
      }
      if (!formData.relievedDate) {
        errors.relievedDate =
          "Relieved Date is required for resigned/relieved employees";
      }
      if (!formData.reason) {
        errors.reason =
          "Resignation Reason is required for resigned/relieved employees";
      }
    }

    if (formData.employmentStatus === "Terminated") {
      if (!formData.terminationDate) {
        errors.terminationDate =
          "Termination Date is required for terminated employees";
      }
      if (!formData.reason) {
        errors.reason =
          "Termination Reason is required for terminated employees";
      }
    }

    // Remove null values
    Object.keys(errors).forEach((key) => {
      if (errors[key] === null) {
        delete errors[key];
      }
    });

    // Set the errors state and return a boolean indicating form validity.
    setFormErrors(errors);
    setIsFormValid(Object.keys(errors).length === 0);

    return Object.keys(errors).length === 0;
  };

  // Handle field blur - validate individual field when user leaves the field
  const handleFieldBlur = (fieldName, value) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));

    const error = validateField(fieldName, value);

    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  // Handle field change - validate and remove errors when value becomes valid
  const handleFieldChange = (fieldName, value) => {
    // Validate on change if field has been touched
    if (touchedFields[fieldName]) {
      const error = validateField(fieldName, value);
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    }
  };

  // Handle field change with immediate validation (for real-time feedback)
  const handleFieldChangeWithValidation = (fieldName, value) => {
    // Mark field as touched and validate immediately
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));

    const error = validateField(fieldName, value);

    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));

    // If dateOfBirth changes, re-validate joiningDate
    if (fieldName === "dateOfBirth" && formData.joiningDate) {
      const joiningDateError = validateJoiningDate(formData.joiningDate, value);
      setFormErrors((prev) => ({
        ...prev,
        joiningDate: joiningDateError,
      }));
      setTouchedFields((prev) => ({ ...prev, joiningDate: true }));
    }
  };

  // Handle employment status change and clear related fields
  const handleEmploymentStatusChange = (newStatus) => {
    if (
      formData?.id &&
      initialEmploymentStatus === "Confirmed" &&
      newStatus === "Probation"
    ) {
      setShowConfirmedToProbationWarning(true);
      return;
    }
    if (
      formData?.id &&
      initialEmploymentStatus === "Relieved" &&
      formData?.employmentStatus === "Relieved" &&
      newStatus !== "Relieved" &&
      isRelievedDateOnOrBeforeToday(initialEmploymentDates.relievedDate)
    ) {
      setShowRelievedLockedWarning(true);
      return;
    }
    setEmployeeData((prev) => {
      const updatedData = { ...prev, employmentStatus: newStatus };

      // Clear related fields based on status change
      if (newStatus !== "Confirmed") {
        updatedData.confirmationDate = null;
      }

      if (newStatus !== "Resigned" && newStatus !== "Relieved") {
        updatedData.resignationDate = null;
      }

      // Clear relievedDate when status is Terminated (use terminationDate instead)
      if (newStatus === "Terminated") {
        updatedData.relievedDate = null;
      } else if (
        newStatus !== "Resigned" &&
        newStatus !== "Relieved" &&
        newStatus !== "Terminated"
      ) {
        updatedData.relievedDate = null;
      }

      // Clear terminationDate when status is not Terminated
      if (newStatus !== "Terminated") {
        updatedData.terminationDate = null;
      }

      if (
        newStatus !== "Resigned" &&
        newStatus !== "Relieved" &&
        newStatus !== "Terminated"
      ) {
        updatedData.reason = null;
      }

      return updatedData;
    });

    // Clear form errors for cleared fields
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (newStatus !== "Confirmed") {
        delete newErrors.confirmationDate;
      }
      if (newStatus !== "Resigned" && newStatus !== "Relieved") {
        delete newErrors.resignationDate;
      }
      // Clear relievedDate error when status is Terminated (use terminationDate instead)
      if (newStatus === "Terminated") {
        delete newErrors.relievedDate;
      } else if (
        newStatus !== "Resigned" &&
        newStatus !== "Relieved" &&
        newStatus !== "Terminated"
      ) {
        delete newErrors.relievedDate;
      }
      
      // Clear terminationDate error when status is not Terminated
      if (newStatus !== "Terminated") {
        delete newErrors.terminationDate;
      }
      
      if (
        newStatus !== "Resigned" &&
        newStatus !== "Relieved" &&
        newStatus !== "Terminated"
      ) {
        delete newErrors.reason;
      }
      return newErrors;
    });

    // Only validate fields that are now required based on the new status
    // Don't validate fields that are not yet filled to avoid showing premature errors
    setTimeout(() => {
      if (newStatus === "Confirmed" && formData.confirmationDate) {
        handleFieldChangeWithValidation(
          "confirmationDate",
          formData.confirmationDate
        );
      }
      if (newStatus === "Resigned" || newStatus === "Relieved") {
        if (formData.resignationDate) {
          handleFieldChangeWithValidation(
            "resignationDate",
            formData.resignationDate
          );
        }
        if (formData.relievedDate) {
          handleFieldChangeWithValidation(
            "relievedDate",
            formData.relievedDate
          );
        }
        if (formData.reason) {
          handleFieldChangeWithValidation("reason", formData.reason);
        }
      }
      if (newStatus === "Terminated") {
        if (formData.terminationDate) {
          handleFieldChangeWithValidation(
            "terminationDate",
            formData.terminationDate
          );
        }
        if (formData.reason) {
          handleFieldChangeWithValidation("reason", formData.reason);
        }
      }

      // Don't validate the entire form here - only validate the specific fields that changed
      // This prevents showing errors for fields that haven't been filled yet
    }, 100);
  };

  const handleInputChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      let fieldValue = type === "checkbox" ? checked : value;

      // Special handling for employment status
      if (name === "employmentStatus") {
        handleEmploymentStatusChange(value);
        return;
      }

      // Auto-capitalize Employee Code
      if (name === "empCode") {
        fieldValue = fieldValue.toUpperCase();
      }

      // Auto-capitalize PAN Number (backend expects uppercase: ABCDE1234F); keep canonical sentinels
      if (name === "panNumber" && typeof fieldValue === "string") {
        const canon = canonicalizePanValue(fieldValue);
        if (!isPanSentinelForUniqueness(canon)) {
          fieldValue = fieldValue.replace(/\s/g, "").toUpperCase();
        } else {
          fieldValue = canon;
        }
      }

      // Aadhaar typing guard: numbers only, max 12 digits.
      if (name === "aadharNumber" && typeof fieldValue === "string") {
        fieldValue = fieldValue.replace(/\D/g, "").slice(0, 12);
      }

      setEmployeeData((prev) => ({
        ...prev,
        [name]: fieldValue,
      }));
      // Validate field immediately for real-time feedback
      handleFieldChangeWithValidation(name, fieldValue);
    },
    [setEmployeeData]
  );

  const handleSave = async (options = {}) => {
    const skipWarning = options.skipWarning === true;
    const forceConfirmed = options.forceConfirmed === true;
    if (readOnly && !isEmployeeView) {
      return { saved: false };
    }
    // For employee view, just navigate to next tab without any API calls
    if (isEmployeeView && onNext && !isLastTab) {
      onNext();
      return;
    }

    if (
      formData?.id &&
      initialEmploymentStatus === "Confirmed" &&
      formData.employmentStatus === "Probation"
    ) {
      setShowConfirmedToProbationWarning(true);
      return { saved: false };
    }

    // Validate entire form when save is clicked (only for admin users)
    const isValid = validateForm();

    if (!isValid) {
      const mobileConflict = isSameIndianMobile(
        formData.user?.phone,
        formData.emergencyContactPhone
      );
      toast.error(
        mobileConflict
          ? MOBILE_SAME_AS_EMERGENCY_CONTACT
          : "Please fix the validation errors before saving"
      );
      // Scroll to the first error
      const firstErrorField = Object.keys(formErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
      // Throw an error to indicate validation failure
      throw new Error("Validation failed");
    }

    const companyForCap = masterData?.company || getAuthJson("companyData", null);
    const isNewEmployeeRecord = !employeeData.id;
    if (isNewEmployeeRecord) {
      const employeeCapError = getEmployeeCapError(companyForCap);
      if (employeeCapError) {
        toast.error(employeeCapError);
        throw new Error(employeeCapError);
      }
    }
    if (formData.essEnabled) {
      const essCapError = getEssCapError(companyForCap);
      if (essCapError) {
        toast.error(essCapError);
        throw new Error(essCapError);
      }
    }

    // Check if master details have changed and show warning (only once until confirmed)
    const masterFields = [
      "branchId",
      "designationId",
      "subDepartmentId",
      "departmentId",
      "gradeId",
      "categoryId",
      "reportingManagerId"
    ];

    const hasMasterChanges =
      employeeData?.id &&
      Object.keys(initialMasterDetails).length > 0 &&
      masterFields.some(f => employeeData[f] !== initialMasterDetails[f]);

    if (!skipWarning && hasMasterChanges && !confirmedMasterChange) {

      setShowMasterDetailsWarning(true);
      
      return { warning: "MASTER_DETAILS_WARNING" };
    }

    const oldJoiningDate = parseDateSafe(initialEmploymentDates.joiningDate);
    const oldRelievedDate = parseDateSafe(initialEmploymentDates.relievedDate);
    const newJoiningDate = parseDateSafe(formData.joiningDate);
    const newRelievedDate = parseDateSafe(formData.relievedDate);
    const joiningChanged =
      oldJoiningDate && newJoiningDate
        ? oldJoiningDate.getTime() !== newJoiningDate.getTime()
        : initialEmploymentDates.joiningDate !== formData.joiningDate;
    const relievedChanged =
      oldRelievedDate && newRelievedDate
        ? oldRelievedDate.getTime() !== newRelievedDate.getTime()
        : initialEmploymentDates.relievedDate !== formData.relievedDate;
    const dateWindowChanged =
      Boolean(employeeData?.id) && (joiningChanged || relievedChanged);

    if (dateWindowChanged) {
      const { blocked, lockedMonths: lockedMonthsInRange } =
        await checkLockedAttendanceForEmploymentDateChange({
          oldJoiningDate: initialEmploymentDates.joiningDate,
          oldRelievedDate: initialEmploymentDates.relievedDate,
          newJoiningDate: formData.joiningDate,
          newRelievedDate: formData.relievedDate,
          fetchAttendanceLocks: attendanceStructureService.getAttendanceLocks,
        });

      if (blocked) {
        setLockedAttendanceWarningMonths(lockedMonthsInRange);
        setShowLockedAttendanceDateWarning(true);
        toast.error(buildLockedAttendanceDateToast(lockedMonthsInRange), {
          duration: 9000,
        });
        const labels = lockedMonthsInRange.map((m) => m.label).join(", ");
        toast.error(
          `Attendance is locked for: ${labels}. Unlock those month(s) in Attendance, then update Date of Join or Relieving Date.`,
          { duration: 9000 }
        );
        return { saved: false };
      }
    }


    // Prevent duplicate identity data across employees.
    // Performance: skip duplicate-check API calls if identity fields are unchanged in edit mode.
    const currentIdentitySnapshot = {
      officialEmail: normalizeEmailForCompare(formData.email),
      personalEmail: normalizeEmailForCompare(formData.user?.email),
      pan: normalizePanForCompare(formData.panNumber),
      aadhaar: normalizeAadhaarForCompare(formData.aadharNumber),
      mobile: normalizeMobileForCompare(formData.user?.phone),
    };
    const identityChanged = Object.keys(currentIdentitySnapshot).some(
      (k) => currentIdentitySnapshot[k] !== initialIdentitySnapshot[k]
    );
    if (!employeeData?.id || identityChanged) {
      // Rejoin flow can only reuse values from the selected source employee.
      await validateUniqueIdentityFields();
    }

    // Prepare data matching backend API expectations
    const nameForSave = normalizeNameInput(formData.user?.name ?? "") || formData.user?.name;
    const isRejoin = Boolean(rejoinSelectedEmployeeId);
    if (isRejoin && isRelievedDateAfterToday(formData?.rejoinSourceRelievedDate)) {
      setShowRejoinBeforeRelieveWarning(true);
      return { saved: false };
    }

    const panForSave = canonicalizePanValue(formData.panNumber || "");

    const sectionData = {
      // Rejoin: backend creates new user + new employee and locks old record
      ...(isRejoin && { rejoinFromEmployeeId: rejoinSelectedEmployeeId }),
      // Employee basic fields (only those expected by backend)
      departmentId: formData.departmentId,
      ...(!isOnboardingMode && { empCode: formData.empCode }),
      designationId: formData.designationId,
      subDepartmentId: formData.subDepartmentId,
      gradeId: formData.gradeId,
      categoryId: formData.categoryId,
      reportingManagerId: isOrgHead
        ? null
        : isOnboardingMode || totalEmployees > 0
          ? formData.reportingManagerId
          : formData.reportingManagerId || null,
      branchId: formData.branchId,
      employmentType: formData.employmentType,
      employmentStatus: isRejoin ? "Probation" : formData.employmentStatus,
      // PAN sentinels: PANNOTAVBL / PANAPPLIED; real PANs stored uppercase.
      panNumber: panForSave,
      aadharNumber: formData.aadharNumber, // Backend expects 'aadhaarNo'
      officialEmail: formData.email, // Backend expects 'officialEmail'
      // personalEmail: formData.personalEmail, // Backend expects 'personalEmail'
      joiningDate: formData.joiningDate, // Backend expects 'dateOfJoin'
      confirmationDate: isRejoin ? null : formData.confirmationDate,
      resignationDate: isRejoin ? null : formData.resignationDate,
      // For rejoin or Terminated status, send terminationDate and set relievedDate to null
      relievedDate: isRejoin || formData.employmentStatus === "Terminated" ? null : formData.relievedDate,
      terminationDate: isRejoin || formData.employmentStatus !== "Terminated" ? null : formData.terminationDate,
      reason: isRejoin ? null : formData.reason,
      essEnabled: formData.essEnabled !== undefined ? !!formData.essEnabled : false,
      inviteSent: formData.inviteSent !== undefined ? !!formData.inviteSent : false,
      ctc: formData.ctc,
      remarks: formData.remarks || null,
      // Statutory Compliance fields
      pfApplicable: formData.pfApplicable !== false, // Default to true
      ceilingPf: formData.ceilingPf !== false, // Default to true
      epsApplicable: formData.epsApplicable || false, // Default to false
      esiApplicable: formData.esiApplicable || false, // Default to false
      ptApplicable: formData.ptApplicable !== false, // Default to true
      gratuityApplicable: formData.gratuityApplicable !== false, // Default to true
      // User details (for user update) - using backend expected field names
      name: nameForSave, // Normalized (trim + collapse spaces), capitalization preserved e.g. "Kranthi Dev Sai"
      phone: formData.user?.phone, // Backend expects 'mobileNumber'
      dateOfBirth: formData.user?.dateOfBirth,
      gender: formData.user?.gender,
      bloodGroup: formData.user?.bloodGroup,
      personalEmail: formData.user?.email, // Backend expects 'email' for personal email
      forceAttendanceRecalculation: false,
      lockedAttendanceMonths: [],
    };

    // Call onSaveSection with error handling for unique constraints
    const wasNewEmployee = !employeeData.id;
    try {
      const savePayload = await onSaveSection(sectionData);
      const createdId =
        savePayload?.id ||
        savePayload?.data?.id ||
        employeeData.id ||
        null;

      // New hire: apply matched salary structure automatically after create
      let structureApplied = false;
      if (wasNewEmployee && canViewSalaryStructure && !isOnboardingMode && createdId) {
        try {
          const applyRes = await applyMatchedSalaryStructureForEmployee({
            employee: {
              id: createdId,
              firstName: formData.firstName || formData.user?.name,
              lastName: formData.lastName || formData.user?.lastName,
              employeeCode: formData.empCode || formData.employeeCode,
              empCode: formData.empCode || formData.employeeCode,
              ctc: formData.ctc,
              branchId: formData.branchId,
              departmentId: formData.departmentId,
              designationId: formData.designationId,
              gradeId: formData.gradeId,
              categoryId: formData.categoryId,
              subDepartmentId: formData.subDepartmentId,
              employmentType: formData.employmentType,
              pfApplicable: formData.pfApplicable,
              esiApplicable: formData.esiApplicable,
              ptApplicable: formData.ptApplicable,
              gratuityApplicable: formData.gratuityApplicable,
              ceilingPf: formData.ceilingPf !== false,
            },
            orgSalaryMethod,
          });
          if (applyRes?.ok) {
            structureApplied = true;
            setEmployeeData((prev) => ({
              ...prev,
              id: createdId,
              salaryStructureSaved: true,
              needsSalaryStructure: false,
            }));
          } else {
            setEmployeeData((prev) => ({
              ...prev,
              id: createdId,
              salaryStructureSaved: false,
              needsSalaryStructure: true,
            }));
            setSalaryStructureAutoOpenKey((k) => k + 1);
            if (applyRes?.reason === "no_structure") {
              toast.error(
                "No salary structure matches this employee. Assign a structure under Employee → Settings → Structures, then save from the eye icon.",
                { duration: 7000 }
              );
            } else if (applyRes?.message) {
              toast.error(applyRes.message);
            } else {
              toast.error(
                "Could not auto-apply salary structure. Use the eye icon to review and Save.",
                { duration: 6000 }
              );
            }
          }
        } catch (applyErr) {
          setEmployeeData((prev) => ({
            ...prev,
            id: createdId,
            salaryStructureSaved: false,
            needsSalaryStructure: true,
          }));
          setSalaryStructureAutoOpenKey((k) => k + 1);
          toast.error(
            applyErr?.message ||
              "Could not auto-apply salary structure. Use the eye icon to review and Save."
          );
        }
      }

      // Show success toast message first
      if (employeeData.id) {
        toast.success("Employee updated successfully");
      } else if (isOnboardingMode) {
        toast.success("Organization details saved");
      } else if (wasNewEmployee && canViewSalaryStructure && structureApplied) {
        toast.success("Employee onboarded and salary structure applied.");
      } else if (wasNewEmployee && canViewSalaryStructure && !structureApplied) {
        toast.success(
          "Employee created. Complete salary structure from the eye icon to finish compensation setup.",
          { duration: 6000 }
        );
      } else {
        toast.success("Employee onboarded successfully");
      }

      try {
        onSaveSuccess?.();
      } catch (_e) {
        /* ignore parent callback errors */
      }

      // After successful save, clear confirmation flag and snapshot current masters
      const wasConfirmed = forceConfirmed || confirmedMasterChange;
      if (employeeData.id || wasNewEmployee) {
        setInitialEmploymentDates({
          joiningDate: formData.joiningDate || null,
          relievedDate: formData.relievedDate || null,
        });
        setInitialIdentitySnapshot(currentIdentitySnapshot);
      }
      setLockedAttendanceWarningMonths([]);
      if (wasConfirmed) {
        setConfirmedMasterChange(false);
        setInitialMasterDetails({
          branchId: formData.branchId,
          designationId: formData.designationId,
          subDepartmentId: formData.subDepartmentId,
          departmentId: formData.departmentId,
          gradeId: formData.gradeId,
          categoryId: formData.categoryId,
          reason: formData.reason,
        });
        // Do NOT navigate further
        return { saved: true, created: wasNewEmployee }; // prevent onNext navigation
      }

      // Structure still required — keep modal open for eye/Save
      if (
        wasNewEmployee &&
        canViewSalaryStructure &&
        !isOnboardingMode &&
        !structureApplied
      ) {
        return { saved: true, created: true, needsSalaryStructure: true };
      }

      // Navigate to next tab after successful save (if not last tab)
      // Original functionality: form's handleSave calls onNext after successful save
      if (!isLastTab && onNext) {
        onNext();
      }
      if (isOnboardingMode && wasNewEmployee) {
        return { saved: true, onboardingOrgSubmitted: true };
      }
      return { saved: true, created: wasNewEmployee, salaryStructureSaved: structureApplied };
    } catch (error) {
      const backendErrors = error.response?.data?.errors || {};
      const sameAsEmergencyFieldError =
        backendErrors.phone ||
        backendErrors.mobileNumber ||
        backendErrors["user.phone"] ||
        backendErrors.emergencyContactPhone;
      const sameAsEmergency =
        typeof sameAsEmergencyFieldError === "string" &&
        /emergency contact/i.test(sameAsEmergencyFieldError)
          ? MOBILE_SAME_AS_EMERGENCY_CONTACT
          : null;
      // Show error toast message
      const errorMessage =
        sameAsEmergency ||
        error.response?.data?.message ||
        error.message ||
        "An error occurred while saving employee details";
      toast.error(errorMessage, {
        duration: error?.response?.data?.code === "EXIT_DATE_PAYROLL_CONFLICT" ? 12000 : 4000,
        style:
          error?.response?.data?.code === "EXIT_DATE_PAYROLL_CONFLICT"
            ? { whiteSpace: "pre-line", maxWidth: 480 }
            : undefined,
      });

      // Handle validation errors from backend (including Sequelize validation errors)
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        const newFormErrors = { ...formErrors };

        // Map backend error fields to frontend field names
        // Note: Backend already maps fields correctly based on model (Employee vs User)
        // Backend sends: 'email' for Employee.email (Official Email) and 'personalEmail' for User.email (Personal Email)
        Object.keys(backendErrors).forEach((field) => {
          // Backend already handles the mapping, so we can use the field name directly
          // But we still need to handle some edge cases and legacy field names
          let frontendField = field;
          
          // Field mapping from backend to frontend
          // Backend now correctly maps:
          // - Employee.email -> 'email' (Official Email)
          // - User.email -> 'personalEmail' (Personal Email)
          const fieldMapping = {
            // Email fields - backend already maps correctly, but keep for safety
            'email': 'email', // Backend sends 'email' for Employee.email (Official Email)
            'personalEmail': 'personalEmail', // Backend sends 'personalEmail' for User.email
            'officialEmail': 'email', // Legacy/fallback mapping
            'user.email': 'personalEmail', // Legacy/fallback mapping
            
            // Other fields
            'phone': 'mobileNumber',
            'mobileNumber': 'mobileNumber',
            'user.phone': 'mobileNumber',
            'emergencyContactPhone': 'mobileNumber',
            'name': 'name',
            'user.name': 'name',
            'dateOfBirth': 'dateOfBirth',
            'user.dateOfBirth': 'dateOfBirth',
            'gender': 'gender',
            'user.gender': 'gender',
            'bloodGroup': 'bloodGroup',
            'user.bloodGroup': 'bloodGroup',
            'panNumber': 'panNumber',
            'aadharNumber': 'aadharNumber',
            'aadhaarNumber': 'aadharNumber',
            'empCode': 'empCode',
            'joiningDate': 'joiningDate',
          };
          
          // Apply mapping if exists, otherwise use field as-is (backend already mapped it correctly)
          if (fieldMapping[field]) {
            frontendField = fieldMapping[field];
          }
          
          const rawErr = backendErrors[field];
          newFormErrors[frontendField] =
            frontendField === "mobileNumber" &&
            typeof rawErr === "string" &&
            /emergency contact/i.test(rawErr)
              ? MOBILE_SAME_AS_EMERGENCY_CONTACT
              : rawErr;
          
          // Mark field as touched so error displays immediately
          setTouchedFields((prev) => ({ ...prev, [frontendField]: true }));
        });

        // Update form errors state
        setFormErrors(newFormErrors);
        
        // Scroll to first error field
        const firstErrorField = Object.keys(newFormErrors)[0];
        if (firstErrorField) {
          setTimeout(() => {
            const element = document.getElementById(firstErrorField) || 
                          document.querySelector(`[name="${firstErrorField}"]`) ||
                          document.querySelector(`input[id*="${firstErrorField}"]`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
              element.focus();
            }
          }, 100);
        }
        
        // Re-throw so parent knows save failed and keeps popup open
        throw error;
      }
      
      // Re-throw error so parent can handle it if it's not a validation error
      throw error;
    }
  };

  // Master details confirmation handlers
  const handleMasterDetailsConfirm = () => {
    setShowMasterDetailsWarning(false);
    setConfirmedMasterChange(true);
    // Proceed with save by calling handleSave again (now bypasses warning)
    setTimeout(() => handleSave({ skipWarning: true, forceConfirmed: true }), 0);
  };

  const handleMasterDetailsCancel = () => {
    setShowMasterDetailsWarning(false);
    setConfirmedMasterChange(false);
  };

  const dismissLockedAttendanceDateModal = () => {
    setShowLockedAttendanceDateWarning(false);
    setLockedAttendanceWarningMonths([]);
  };

  // Expose handleSave to parent component via callback
  useEffect(() => {
    if (onSaveTrigger) {
      // Wrap in a function to ensure we always call the latest version
      onSaveTrigger(() => handleSave());
    }
  }, [onSaveTrigger, formData.inviteSent, employeeData.id]); // Re-expose when key data changes

  return (
    <div className="min-h-0 space-y-4">
      {/* Form body; primary actions are in the modal footer (onSaveTrigger). */}
      <fieldset className="min-w-0 border-0 p-0 m-0">
      <div className="profile-field-grid">
        {isOnboardingMode ? (
          <div className="relative w-full min-w-0">
            <FloatingInput
              id="candidateId"
              label="Candidate ID"
              value={formData.candidateId || ""}
              onChange={() => {}}
              placeholder="Auto-generated on save"
              disabled
              readOnly
            />
          </div>
        ) : (
        <div className="relative w-full min-w-0">
          <FloatingInput
            id="empCode"
            label="Employee Code"
            value={formData.empCode || ""}
            onChange={handleInputChange}
            onBlur={(e) => handleFieldBlur("empCode", e.target.value)}
            required
            error={formErrors.empCode}
            placeholder={
              employeeCodeStructure?.patternType === "automated"
                ? "Auto-generated"
                : "Letters, numbers, -, /, . (1-20 chars)"
            }
            maxLength={20}
            disabled={
              readOnly ||
              isEmployeeView ||
              employeeCodeStructure?.patternType === "automated"
            }
            readOnly={
              isEmployeeView ||
              employeeCodeStructure?.patternType === "automated"
            }
          />
          {employeeCodeStructure?.patternType === "automated" && (
            <div className="absolute right-3 top-3 flex items-center">
              {isGeneratingCode ? (
                <div className="animate-spin h-4 w-4 border-2 border-[hsl(var(--ds-primary))] border-t-transparent rounded-full"></div>
              ) : (
                <span className="rounded-full bg-[hsl(var(--ds-success)/0.12)] px-2 py-0.5 text-xs font-medium text-[hsl(var(--ds-success))]">
                  Auto
                </span>
              )}
            </div>
          )}
        </div>
        )}
        <FloatingInput
          id="name"
          label="Name"
          value={formData.user?.name || ""}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const raw = e.target.value;
            const value = toTitleCaseName(raw);
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, name: value },
            }));
            handleFieldChangeWithValidation("name", value);
          }}
          onBlur={(e) => {
            const raw = e.target.value;
            handleFieldBlur("name", raw);
            // Normalize only whitespace (trim + collapse spaces), preserve capitalization e.g. "Kranthi Dev Sai"
            const normalized = normalizeNameInput(raw);
            if (normalized !== raw && !validateName(raw)) {
              setEmployeeData((prev) => ({
                ...prev,
                user: { ...prev.user, name: normalized },
              }));
              setFormErrors((prev) => ({ ...prev, name: null }));
            }
          }}
          required
          error={formErrors.name}
          placeholder="Name"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <SearchableSelect
          id="gender"
          label="Gender"
          value={formData.user?.gender}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, gender: value },
            }));
            handleFieldChangeWithValidation("gender", value);
          }}
          onBlur={(e) => handleFieldBlur("gender", e.target.value)}
          staticOptions={[
            { value: "Male", label: "Male" },
            { value: "Female", label: "Female" },
            { value: "Other", label: "Other" },
          ]}
          required
          error={formErrors.gender}
          disabled={readOnly || isEmployeeView}
        />
        <div className="w-full min-w-0">
          <DatePicker
            id="dateOfBirth"
            label="Date of Birth"
            value={formData.user?.dateOfBirth || ""}
            min={
              new Date(new Date().setFullYear(new Date().getFullYear() - 100))
                .toISOString()
                .split("T")[0]
            }
            max={
              new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                .toISOString()
                .split("T")[0]
            }
            disabled={readOnly || isEmployeeView}
            darkMode={!!darkMode}
            className="w-full"
            required
            error={formErrors.dateOfBirth}
            maxErrorText="Employee must be at least 18 years old"
            minErrorText="Employee age cannot be more than 100 years"
            onChange={(e) => {
              if (isEmployeeView) return;
              const value = e.target.value;
              setEmployeeData((prev) => ({
                ...prev,
                user: { ...prev.user, dateOfBirth: value },
              }));
              handleFieldChangeWithValidation("dateOfBirth", value);
            }}
          />
        </div>
        <SearchableSelect
          id="branchId"
          label="Branch"
          value={formData.branchId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("branchId", e.target.value)}
          staticOptions={(masterData.branches || []).map((branch) => ({
            value: branch.id,
            label: branch.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.branchId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="designationId"
          label="Designation"
          value={formData.designationId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("designationId", e.target.value)}
          staticOptions={(masterData.designations || []).map((designation) => ({
            value: designation.id,
            label: designation.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.designationId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="departmentId"
          label="Department"
          value={formData.departmentId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("departmentId", e.target.value)}
          staticOptions={(masterData.departments || []).map((department) => ({
            value: department.id,
            label: department.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.departmentId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="subDepartmentId"
          label="Sub Department"
          value={formData.subDepartmentId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("subDepartmentId", e.target.value)}
          staticOptions={(masterData.subDepartments || []).map((subDept) => ({
            value: subDept.id,
            label: subDept.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.subDepartmentId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="gradeId"
          label="Grade"
          value={formData.gradeId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("gradeId", e.target.value)}
          staticOptions={(masterData.grades || []).map((grade) => ({
            value: grade.id,
            label: grade.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.gradeId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="categoryId"
          label="Category"
          value={formData.categoryId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("categoryId", e.target.value)}
          staticOptions={(masterData.categories || []).map((category) => ({
            value: category.id,
            label: category.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.categoryId}
          disabled={readOnly || isEmployeeView}
        />

        {/* Reporting Manager — read-only FloatingInput for Head of Organisation */}
        {isOrgHead ? (
          <FloatingInput
            id="reportingManagerId"
            label="Reporting Manager"
            value="Head of Organisation"
            onChange={() => {}}
            disabled
            readOnly
            darkMode={darkMode}
          />
        ) : (
          (isOnboardingMode || totalEmployees > 0) && (
            <SearchableSelect
              id="reportingManagerId"
              label="Reporting Manager"
              value={formData.reportingManagerId}
              onChange={handleInputChange}
              onBlur={(e) =>
                handleFieldBlur("reportingManagerId", e.target.value)
              }
              error={formErrors.reportingManagerId}
              fetchOptions={fetchReportingEmployees}
              darkMode={darkMode}
              isEmployee={true}
              required={
                isOnboardingMode ||
                !(formData.user?.role === "admin" || formData.user?.role === "super_admin")
              }
              disabled={readOnly || isEmployeeView}
            />
          )
        )}
        <DatePicker
          id="joiningDate"
          label="Date of Join"
          value={formData.joiningDate || ""}
          min={(() => {
            if (formData.user?.dateOfBirth) {
              const d = new Date(formData.user.dateOfBirth);
              d.setFullYear(d.getFullYear() + 18);
              return d.toISOString().split("T")[0];
            }
            return "";
          })()}
          max={new Date().toISOString().split("T")[0]}
          disabled={readOnly || isEmployeeView}
          darkMode={!!darkMode}
          className="w-full"
          error={formErrors.joiningDate}
          minErrorText="Employee must be at least 18 years old at joining date"
          maxErrorText="Joining Date cannot be in the future"
          validateOnType={true}
          onChange={(e) => {
            if (isEmployeeView) return;
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              joiningDate: value,
            }));
            handleFieldChangeWithValidation("joiningDate", value);
          }}
        />
        <SearchableSelect
          id="employmentType"
          label="Employee Type"
          value={formData.employmentType}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            handleInputChange({
              target: { name: "employmentType", value: value },
            });
            handleFieldChangeWithValidation("employmentType", value);
          }}
          onBlur={(e) => handleFieldBlur("employmentType", e.target.value)}
          staticOptions={[
            { value: "Full-time", label: "Full-Time" },
            { value: "External", label: "External" },
          ]}
          required
          error={formErrors.employmentType}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="employmentStatus"
          label="Employment Status"
          value={formData.employmentStatus}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            if (
              formData.id &&
              initialEmploymentStatus === "Confirmed" &&
              value === "Probation"
            ) {
              setShowConfirmedToProbationWarning(true);
              return;
            }
            handleEmploymentStatusChange(value);
            // Also mark this field as touched and validate it
            handleFieldChangeWithValidation("employmentStatus", value);
          }}
          onBlur={(e) => handleFieldBlur("employmentStatus", e.target.value)}
          staticOptions={[
            { value: "Probation", label: "Probation" },
            { value: "Confirmed", label: "Confirmed" },
            { value: "Resigned", label: "Resigned" },
            { value: "Relieved", label: "Relieved" },
            { value: "Terminated", label: "Terminated" },
          ]}
          darkMode={darkMode}
          required
          error={formErrors.employmentStatus}
          disabled={readOnly || isEmployeeView}
        />
        {formData.employmentStatus === "Probation" && (
          <div
            className="w-full min-w-0"
            title={
              policyConfirmationPreview.policy && policyConfirmationPreview.date
                ? `From policy "${
                    policyConfirmationPreview.policy.policyName ||
                    policyConfirmationPreview.policy.name
                  }" (${policyConfirmationPreview.probationDays}d from joining date)`
                : !formData.joiningDate
                  ? "Select joining date first"
                  : confirmationPolicies.length > 0
                    ? "No active confirmation policy matches this employee's org details yet"
                    : "Confirmation date is set by the applicable confirmation policy"
            }
          >
            <DatePicker
              id="policyConfirmationDate"
              label="Confirmation Date"
              value={policyConfirmationPreview.date || ""}
              onChange={() => {}}
              disabled
              darkMode={!!darkMode}
              className="w-full"
            />
          </div>
        )}
        {formData.employmentStatus === "Confirmed" && (
          <DatePicker
            id="confirmationDate"
            label="Confirmation Date"
            type={isEmployeeView ? "text" : "date"}
            value={formData.confirmationDate || ""}
            onChange={(e) => {
              if (isEmployeeView) return;
              const v = e.target.value;
              handleInputChange({
                target: { name: "confirmationDate", value: v },
              });
              handleFieldChangeWithValidation("confirmationDate", v);
            }}
            required
            error={formErrors.confirmationDate}
            min={formData.joiningDate || new Date().toISOString().split("T")[0]}
            // max={new Date().toISOString().split('T')[0]}
            disabled={readOnly || isEmployeeView}
            readOnly={isEmployeeView}
          />
        )}
        {(formData.employmentStatus === "Resigned" ||
          formData.employmentStatus === "Relieved") && (
          <>
            <DatePicker
              id="resignationDate"
              value={formData.resignationDate || ""}
              onChange={(e) => {
                if (isEmployeeView) return;
                handleInputChange({
                  target: { name: "resignationDate", value: e.target.value },
                });
                handleFieldChangeWithValidation(
                  "resignationDate",
                  e.target.value
                );
              }}
              label="Date of Resignation"
              type={isEmployeeView ? "text" : "date"}
              onBlur={(e) => handleFieldBlur("resignationDate", e.target.value)}
              required
              min={formData.joiningDate}
              minErrorText="Resignation Date cannot be before Joining Date"
              error={formErrors.resignationDate}
              validateOnType={false}
              disabled={readOnly || isEmployeeView}
            />
            <DatePicker
              id="relievedDate"
              value={formData.relievedDate || ""}
              onChange={(e) => {
                if (isEmployeeView) return;
                handleInputChange({
                  target: { name: "relievedDate", value: e.target.value },
                });
                handleFieldChangeWithValidation("relievedDate", e.target.value);
              }}
              onBlur={(e) => handleFieldBlur("relievedDate", e.target.value)}
              label="Date of Relieved"
              required
              min={formData.resignationDate || formData.joiningDate}
              minErrorText={
                formData.resignationDate
                  ? "Relieved Date cannot be before Resignation Date"
                  : "Relieved Date cannot be before Joining Date"
              }
              error={formErrors.relievedDate}
              validateOnType={false}
              disabled={readOnly || isEmployeeView}
            />
            <SearchableSelect
              id="reasonForResignation"
              label="Resignation Reason"
              value={formData.reason}
              onChange={(e) => {
                if (isEmployeeView) return; // Prevent changes for employee view
                const value = e.target.value;
                handleInputChange({ target: { name: "reason", value: value } });
                handleFieldChangeWithValidation("reason", value);
              }}
              onBlur={(e) => handleFieldBlur("reason", e.target.value)}
              staticOptions={masterData.resignationReasons.map((reason) => ({
                value: reason.id,
                label: reason.name,
              }))}
              darkMode={darkMode}
              required
              error={formErrors.reason}
              disabled={readOnly || isEmployeeView}
            />
          </>
        )}
        {formData.employmentStatus === "Terminated" && (
          <>
            <DatePicker
              value={formData.terminationDate || ""}
              onChange={(e) => {
                if (isEmployeeView) return;
                setEmployeeData((prev) => ({
                  ...prev,
                  terminationDate: e.target.value,
                }));
                handleFieldChangeWithValidation("terminationDate", e.target.value);
              }}
              label="Termination Date"
              required
              min={formData.joiningDate}
              max={new Date().toISOString().slice(0, 10)}
              minErrorText="Termination Date cannot be before Joining Date"
              maxErrorText="Termination Date cannot be in the future"
              error={formErrors.terminationDate}
              validateOnType={false}
              disabled={readOnly || isEmployeeView}
            />
            <SearchableSelect
              id="reasonForTermination"
              label="Termination Reason"
              value={formData.reason}
              onChange={(e) => {
                if (isEmployeeView) return; // Prevent changes for employee view
                const value = e.target.value;
                handleInputChange({ target: { name: "reason", value: value } });
                handleFieldChangeWithValidation("reason", value);
              }}
              onBlur={(e) => handleFieldBlur("reason", e.target.value)}
              staticOptions={masterData.terminationReasons.map((reason) => ({
                value: reason.id,
                label: reason.name,
              }))}
              darkMode={darkMode}
              required
              error={formErrors.reason}
              disabled={readOnly || isEmployeeView}
            />
          </>
        )}
        <FloatingInput
          id="mobileNumber"
          label="Mobile Number"
          value={formData.user?.phone}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, phone: value },
            }));
            handleFieldChangeWithValidation("mobileNumber", value);
          }}
          onBlur={(e) => handleFieldBlur("mobileNumber", e.target.value)}
          required
          error={formErrors.mobileNumber}
          maxLength={10}
          placeholder="10-digit mobile number"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <FloatingInputWithNA
          id="personalEmail"
          label="Personal Email"
          type="email"
          value={formData.user?.email || ""}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, email: value },
            }));
            handleFieldChangeWithValidation("personalEmail", value);
          }}
          onBlur={(e) => handleFieldBlur("personalEmail", e.target.value)}
          required
          error={formErrors.personalEmail}
          placeholder="Personal Email"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <FloatingInputWithNA
          id="email"
          label="Official Email"
          type="email"
          value={formData.email || ""}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("email", e.target.value)}
          required
          error={formErrors.email}
          placeholder="Official Email"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <SearchableSelect
          id="bloodGroup"
          label="Blood Group"
          value={formData.user?.bloodGroup}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, bloodGroup: value },
            }));
            handleFieldChangeWithValidation("bloodGroup", value);
          }}
          onBlur={(e) => handleFieldBlur("bloodGroup", e.target.value)}
          staticOptions={[
            { label: "A+", value: "A+" },
            { label: "A-", value: "A-" },
            { label: "B+", value: "B+" },
            { label: "B-", value: "B-" },
            { label: "AB+", value: "AB+" },
            { label: "AB-", value: "AB-" },
            { label: "O+", value: "O+" },
            { label: "O-", value: "O-" },
            { label: "NA", value: "NA" },
          ]}
          required
          error={formErrors.bloodGroup}
          disabled={readOnly || isEmployeeView}
        />
        <FloatingInput
          id="aadharNumber"
          label="Aadhaar No"
          value={formData.aadharNumber || ""}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("aadharNumber", e.target.value)}
          required
          error={formErrors.aadharNumber}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={12}
          placeholder="12-digit Aadhaar number"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <FloatingInputWithNA
          id="panNumber"
          label="PAN"
          value={formData.panNumber || ""}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("panNumber", e.target.value)}
          required
          error={formErrors.panNumber}
          maxLength={10}
          naOption={PAN_SENTINEL_NOT_AVAILABLE}
          extraOptions={[PAN_SENTINEL_APPLIED]}
          placeholder="PAN Number"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <div className="flex w-full min-w-0 flex-col">
          <div className="relative">
            {canViewSalaryStructure && (
              <div className="absolute right-0 top-2 z-20">
                <EmployeeSalaryStructureModal
                  employee={{
                    id: formData.id,
                    firstName: formData.firstName || formData.user?.name,
                    lastName: formData.lastName || formData.user?.lastName,
                    employeeCode: formData.empCode || formData.employeeCode,
                    empCode: formData.empCode || formData.employeeCode,
                    ctc: formData.ctc,
                    branchId: formData.branchId,
                    departmentId: formData.departmentId,
                    designationId: formData.designationId,
                    gradeId: formData.gradeId,
                    categoryId: formData.categoryId,
                    subDepartmentId: formData.subDepartmentId,
                    employmentType: formData.employmentType,
                    pfApplicable: formData.pfApplicable,
                    esiApplicable: formData.esiApplicable,
                    ptApplicable: formData.ptApplicable,
                    gratuityApplicable: formData.gratuityApplicable,
                    ceilingPf: formData.ceilingPf !== false,
                  }}
                  darkMode={darkMode}
                  disabled={readOnly || isEmployeeView}
                  orgSalaryMethod={orgSalaryMethod}
                  title="View salary structure breakdown"
                  autoOpenKey={salaryStructureAutoOpenKey}
                  onSaved={() => {
                    setEmployeeData((prev) => ({
                      ...prev,
                      salaryStructureSaved: true,
                      needsSalaryStructure: false,
                    }));
                    toast.success(
                      "Salary structure saved. Employee onboarding is complete for compensation."
                    );
                  }}
                />
              </div>
            )}
            <FloatingInput
              id="ctc"
              label={ctcFieldLabel}
              type="number"
              value={formData.ctc || ""}
              onChange={handleInputChange}
              onBlur={(e) => handleFieldBlur("ctc", e.target.value)}
              required
              error={formErrors.ctc}
              min="0"
              step="0.01"
              placeholder={`Enter ${ctcFieldLabel}`}
              disabled={readOnly || isEmployeeView}
              readOnly={isEmployeeView}
            />
            {canChooseStructureMode(orgSalaryMethod) && (
              <div className="mt-1.5 flex items-center gap-4 text-xs text-[hsl(var(--ds-muted-fg))]">
                <span className="font-medium">Amount type</span>
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="profileSalaryMode"
                    checked={profileSalaryMode === "CTC"}
                    disabled={readOnly || isEmployeeView}
                    onChange={() => setProfileSalaryMode("CTC")}
                    className="accent-[hsl(var(--ds-primary))]"
                  />
                  CTC
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="profileSalaryMode"
                    checked={profileSalaryMode === "GROSS"}
                    disabled={readOnly || isEmployeeView}
                    onChange={() => setProfileSalaryMode("GROSS")}
                    className="accent-[hsl(var(--ds-primary))]"
                  />
                  Gross
                </label>
              </div>
            )}
          </div>
          {/* CTC in words – always below the input box */}
          {formData.ctc && parseFloat(formData.ctc) > 0 && (
            <div className={`mt-1 text-left text-xs ${darkMode ? "text-[hsl(var(--ds-accent))]" : "text-[hsl(var(--ds-secondary))]"}`}>
              Rs. {numberToWords(formData.ctc)}
            </div>
          )}

          {canViewSalaryStructure &&
          (formData.salaryStructureSaved || formData.needsSalaryStructure) ? (
            <p
              className={`mt-1 text-[11px] ${
                formData.salaryStructureSaved
                  ? "text-[hsl(var(--ds-success))]"
                  : "text-[hsl(var(--ds-warning))]"
              }`}
            >
              {formData.salaryStructureSaved
                ? "Salary structure saved for this employee."
                : "Salary structure not applied yet — open the eye icon and Save, or fix structure assignment."}
            </p>
          ) : null}
        </div>
        <FloatingInput
          id="remarks"
          label="Remarks"
          value={formData.remarks || ""}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("remarks", e.target.value)}
          placeholder="Remarks (Optional)"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />

        {/* Statutory Applicability moved to Profile Salary Structure (CTC eye) */}
        <div className="col-span-full border-t border-[hsl(var(--ds-border))] pt-3 mt-2">
          <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
            Statutory Applicability (PF, Ceiling PF, EPS, ESI, PT, Gratuity) and structure add-ons
            (Employer NPS, VPF, Meal Voucher) are managed under{" "}
            <strong>Profile Salary Structure</strong> (eye icon on CTC / Gross). After payroll is
            processed or finalised, use <strong>Salary Revision</strong> or{" "}
            <strong>Update Statutory</strong>.
          </p>
        </div>
      </div>

      </fieldset>

      {/* Statutory Warning Modal (kept for any remaining Org Details flows) */}
      {showStatutoryWarningModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[hsl(var(--ds-surface))] rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-[hsl(var(--ds-border))]">
              <h3 className="text-lg font-semibold text-[hsl(var(--ds-fg))]">
                {showStatutoryWarningModal.title}
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="text-sm text-[hsl(var(--ds-fg))] whitespace-pre-line">
                {showStatutoryWarningModal.message}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[hsl(var(--ds-border))] flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowStatutoryWarningModal({ show: false, field: null, message: '', title: '' });
                  setPendingCheckboxChange(null);
                }}
                className="px-4 py-2 bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] rounded hover:bg-[hsl(var(--ds-muted))] focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (pendingCheckboxChange) {
                    if (pendingCheckboxChange.name === 'pfApplicable' && pendingCheckboxChange.checked === false) {
                      setEmployeeData((prev) => ({
                        ...prev,
                        pfApplicable: false,
                        ceilingPf: true,
                      }));
                      handleFieldChangeWithValidation('pfApplicable', false);
                      handleFieldChangeWithValidation('ceilingPf', true);
                    } else {
                      handleInputChange({
                        target: {
                          name: pendingCheckboxChange.name,
                          value: pendingCheckboxChange.checked,
                          type: 'checkbox',
                          checked: pendingCheckboxChange.checked,
                        },
                      });
                    }
                  }
                  setShowStatutoryWarningModal({ show: false, field: null, message: '', title: '' });
                  setPendingCheckboxChange(null);
                }}
                className="px-4 py-2 inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 rounded hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/35 focus:ring-offset-2"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {feedback && (
        <div
          className={`mt-2 text-sm ${
            feedback.type === "success"
              ? "text-[hsl(var(--ds-success))]"
              : "text-[hsl(var(--ds-danger))]"
          }`}
        >
          {typeof feedback.message === "string"
            ? feedback.message
            : feedback.message?.error ||
              feedback.message?.name ||
              "Unexpected error"}
        </div>
      )}

      {/* Validation Summary - Only show when form is submitted or fields are touched */}
      {/* {Object.keys(formErrors).length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
                    <ul className="text-sm text-red-700 space-y-1">
                        {Object.entries(formErrors).map(([field, error]) => (
                            <li key={field} className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>{error}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )} */}

      {/* Confirmed → Probation blocked (same shell as Master Details warning) */}
      {showConfirmedToProbationWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div
            className={`rounded-lg p-6 max-w-md mx-4 shadow-lg ${
              darkMode ? "bg-[hsl(var(--ds-muted))] text-white" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirmed-to-probation-warning-title"
          >
            <div className="mb-4">
              <h3
                id="confirmed-to-probation-warning-title"
                className="mb-2 text-lg font-semibold text-amber-700"
              >
                Employment Status Change
              </h3>
              <p className="text-sm">
                You are not allowed from confirmation to probation.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowConfirmedToProbationWarning(false)}
                className="px-4 py-2 text-sm font-medium text-[hsl(var(--ds-fg))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))] rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowConfirmedToProbationWarning(false)}
                className="rounded-md bg-[hsl(var(--ds-warning))] px-4 py-2 text-sm font-medium text-[hsl(var(--ds-warning-fg))] transition-colors hover:opacity-90"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showRelievedLockedWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div
            className={`rounded-lg p-6 max-w-md mx-4 shadow-lg ${
              darkMode ? "bg-[hsl(var(--ds-muted))] text-white" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="relieved-locked-warning-title"
          >
            <div className="mb-4">
              <h3
                id="relieved-locked-warning-title"
                className="mb-2 text-lg font-semibold text-amber-700"
              >
                Employment Status Change
              </h3>
              <p className="text-sm">
                This employee is in Relieved status and the date of relieved is
                on or before today. You cannot change employment status to any
                other value.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowRelievedLockedWarning(false)}
                className="px-4 py-2 text-sm font-medium text-[hsl(var(--ds-fg))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))] rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowRelievedLockedWarning(false)}
                className="rounded-md bg-[hsl(var(--ds-warning))] px-4 py-2 text-sm font-medium text-[hsl(var(--ds-warning-fg))] transition-colors hover:opacity-90"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejoinBeforeRelieveWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div
            className={`rounded-lg p-6 max-w-md mx-4 shadow-lg ${
              darkMode ? "bg-[hsl(var(--ds-muted))] text-white" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rejoin-before-relieve-warning-title"
          >
            <div className="mb-4">
              <h3
                id="rejoin-before-relieve-warning-title"
                className="text-lg font-semibold text-yellow-600 mb-2"
              >
                Rejoin Not Allowed
              </h3>
              <p className="text-sm">
                Rejoin cannot be done before the selected employee&apos;s relieved date.
                Please wait until the relieved date is reached, then try again.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowRejoinBeforeRelieveWarning(false)}
                className="px-4 py-2 text-sm font-medium text-[hsl(var(--ds-fg))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))] rounded-md transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Master Details Warning Modal */}
      {showMasterDetailsWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`bg-[hsl(var(--ds-surface))] rounded-lg p-6 max-w-md mx-4 ${darkMode ? 'bg-[hsl(var(--ds-muted))] text-white' : 'bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]'}`}>
            <div className="mb-4">
              <h3 className="mb-2 text-lg font-semibold text-amber-700">Master Details Change Warning</h3>
              <p className="text-sm">
                You are editing master details. This means the masters details are applicable from date of joining and history cannot be retrieved for master details.
                {" "}
                If you wish to change master details we suggest to change from
                {" "}
                {onNavigateToTransfer ? (
                  <button
                    type="button"
                    className="underline text-[hsl(var(--ds-secondary))] hover:text-[hsl(var(--ds-secondary))]-dark ml-1 mr-1"
                    onClick={() => {
                      setShowMasterDetailsWarning(false);
                      if (typeof onCancel === "function") {
                        onCancel();
                      }
                      onNavigateToTransfer();
                    }}
                  >
                    Actions → Transfer
                  </button>
                ) : (
                  <span className="font-semibold"> actions transfer </span>
                )}
                instead. Continue with caution.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleMasterDetailsCancel}
                className="px-4 py-2 text-sm font-medium text-[hsl(var(--ds-fg))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))] rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMasterDetailsConfirm}
                className="rounded-md bg-[hsl(var(--ds-warning))] px-4 py-2 text-sm font-medium text-[hsl(var(--ds-warning-fg))] transition-colors hover:opacity-90"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <LockedAttendanceDateWarningModal
        open={showLockedAttendanceDateWarning}
        lockedMonths={lockedAttendanceWarningMonths}
        darkMode={darkMode}
        onDismiss={dismissLockedAttendanceDateModal}
      />
    </div>
  );
}
